import { createEntityQuery, createQuery, type EntityStore, filterNilValue, getStoreByName } from '@datorama/akita';
import { diff } from 'deep-object-diff';
import { isEmpty, omit, pickBy } from 'lodash-es';
import { of, switchMap, tap } from 'rxjs';
import theStore from './store';
import { type EntityBase, type FpCalculation } from 'types/models';
import { ApprovalStatus } from 'utils/enum';

export const query = createQuery(theStore);

export const history$ = query.select(['storeName', 'entityId', 'footprintId']).pipe(
   tap(({ storeName, entityId }) => {
      if (storeName && entityId) {
         const relevantStore = getStoreByName<EntityStore>(storeName);

         if (relevantStore) {
            const storeQuery = createEntityQuery(relevantStore);

            return storeQuery
               .selectAll({ limitTo: 1, filterBy: (entity: any) => entity.id === entityId && Array.isArray(entity.history) })
               .pipe(filterNilValue())
               .subscribe();
         }
      }
   }),
   switchMap(({ storeName, entityId, footprintId }) => {
      if (storeName && entityId) {
         const relevantStore = getStoreByName<EntityStore>(storeName);

         if (relevantStore) {
            const storeQuery = createEntityQuery(relevantStore);
            let searchId = entityId;

            if (storeQuery?.__store__?.storeName === 'emission/footprints' && footprintId) {
               searchId = footprintId;
            }

            return storeQuery.selectEntity(searchId, (object: any) => {
               let history = object?.history as EntityBase['history'];
               if (storeQuery?.__store__?.storeName === 'emission/footprints' && footprintId) {
                  history = object?.calculations.find((calc: FpCalculation) => calc?.id === entityId)?.history;
               }

               return Array.isArray(history)
                  ? history
                       .map((historyEntry) => {
                          const oldValue =
                             typeof (historyEntry?.oldValue ?? '{}') === 'string'
                                ? JSON.parse(historyEntry?.oldValue ?? '{}')
                                : { ...(historyEntry?.oldValue ?? {}) };

                          const newValue =
                             typeof (historyEntry?.newValue ?? '{}') === 'string'
                                ? JSON.parse(historyEntry?.newValue ?? '{}')
                                : { ...(historyEntry?.newValue ?? {}) };

                          const timeline: Record<string, any> = diff(oldValue, newValue);

                          const changed = {
                             ...omit(timeline, ['justification']),
                             ...('justification' in timeline ? { justification: timeline.justification } : {}),
                          };

                          const localizedKeyNames = [
                             'name',
                             'desc',
                             'shortDesc',
                             'recommendation',
                             'placeholder',
                             'guidance',
                             'auditorGuidance',
                             'individualGuidance',
                          ];

                          const newObj: Record<string, any> = { ...pickBy(historyEntry, (attr) => attr !== undefined && attr !== null), changed };

                          localizedKeyNames.forEach((localizedKey) => {
                             const localizedKeys = Object.keys(timeline).filter((key) => key.startsWith(`${localizedKey}_`));
                             const areValuesEqual =
                                localizedKeys.length > 0 && localizedKeys.every((key) => timeline[key] === timeline[localizedKeys[0]]);

                             const oldValuesEqual =
                                localizedKeys.length > 0 && localizedKeys.every((key) => oldValue[key] === oldValue[localizedKeys[0]]);

                             const newValuesEqual =
                                localizedKeys.length > 0 && localizedKeys.every((key) => newValue[key] === newValue[localizedKeys[0]]);

                             if (areValuesEqual) {
                                newObj[localizedKey] = timeline[localizedKeys[0]];
                                localizedKeys.forEach((keytoDelete) => delete newObj[keytoDelete]);
                             }

                             if (newValuesEqual && oldValuesEqual) {
                                newValue[localizedKey] = newValue[localizedKeys[0]];
                                localizedKeys.forEach((keytoDelete) => delete newValue[keytoDelete]);

                                oldValue[localizedKey] = oldValue[localizedKeys[0]];
                                localizedKeys.forEach((keytoDelete) => delete oldValue[keytoDelete]);

                                newObj.changed[localizedKey] = newObj.changed[localizedKeys[0]];
                                localizedKeys.forEach((keytoDelete) => delete newObj.changed[keytoDelete]);
                             }
                          });

                          if (oldValue?.status !== newValue?.status) {
                             const statusChange = `${oldValue.status ?? ''}->${newValue.status}`;

                             let statusDesc;

                             switch (statusChange) {
                                case 'IN_PROGRESS->REVIEWED':
                                   statusDesc = ApprovalStatus.REVIEWED;
                                   break;
                                case 'REVIEWED->APPROVED':
                                   statusDesc = ApprovalStatus.APPROVED;
                                   break;
                                case 'APPROVED->ARCHIVED':
                                   statusDesc = ApprovalStatus.ARCHIVED;
                                   break;
                                case 'ARCHIVED->APPROVED':
                                   statusDesc = 'UNARCHIVE';
                                   break;
                                case 'APPROVED->IN_PROGRESS':
                                   statusDesc = 'UNAPPROVE';
                                   break;
                                case 'REVIEWED->IN_PROGRESS':
                                   statusDesc = 'UNREVIEW';
                                   break;
                                default:
                                   break;
                             }

                             newObj.kind = (statusDesc ?? newObj?.kind ?? newObj?.desc ?? '').toUpperCase();
                          }

                          return omit(
                             {
                                ...newObj,
                                oldValue,
                                newValue,
                             },
                             ['slug', 'createdAt', 'level', 'organisationId', 'objectId']
                          );
                       })
                       .filter(
                          (historyEntry: any) =>
                             !isEmpty(omit(historyEntry.changed, ['id', 'format'])) &&
                             !(
                                Object.keys(historyEntry.oldValue).length <= 2 &&
                                Object.keys(historyEntry.oldValue).includes('status') &&
                                historyEntry.table === 'businessactivity_taxonomyactivity'
                             )
                       )
                  : [];
            });
         }
      }
      return of([]);
   })
);

export const loading$ = query.selectLoading();
